import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
    MapPin, Car, Navigation, Trash2, Clock, 
    ParkingCircle, Locate, Save, X, Plus, History
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { el, enUS } from 'date-fns/locale';

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom parking pin icon
const createParkingIcon = (isActive = true) => {
    const color = isActive ? '#8b5cf6' : '#94a3b8';
    const iconHtml = `
        <div style="
            background-color: ${color};
            width: 40px;
            height: 40px;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            display: flex;
            align-items: center;
            justify-content: center;
            border: 3px solid white;
            box-shadow: 0 3px 8px rgba(0,0,0,0.3);
        ">
            <span style="transform: rotate(45deg); font-size: 18px;">🅿️</span>
        </div>
    `;
    return L.divIcon({
        html: iconHtml,
        className: 'parking-marker',
        iconSize: [40, 40],
        iconAnchor: [20, 40],
        popupAnchor: [0, -40]
    });
};

const activeParkingIcon = createParkingIcon(true);
const historyParkingIcon = createParkingIcon(false);

// Component to handle map clicks for placing pins
function MapClickHandler({ onMapClick, isPlacingPin }) {
    useMapEvents({
        click: (e) => {
            if (isPlacingPin) {
                onMapClick(e.latlng);
            }
        }
    });
    return null;
}

// Component to center map on location
function CenterMap({ center }) {
    const map = useMap();
    useEffect(() => {
        if (center) {
            map.setView(center, 16);
        }
    }, [center, map]);
    return null;
}

export default function ParkingMapPage() {
    const { language } = useLanguage();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [parkingLocations, setParkingLocations] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState('');
    
    // Map state
    const [mapCenter, setMapCenter] = useState([37.9838, 23.7275]); // Athens default
    const [isPlacingPin, setIsPlacingPin] = useState(false);
    const [tempPin, setTempPin] = useState(null);
    
    // Modal state
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [savingLocation, setSavingLocation] = useState(false);
    const [locationNote, setLocationNote] = useState('');
    
    // Current active parking
    const [activeParking, setActiveParking] = useState(null);

    const labels = {
        title: language === 'el' ? 'Χάρτης Στάθμευσης' : 'Parking Map',
        subtitle: language === 'el' ? 'Σημειώστε πού παρκάρατε το όχημά σας' : 'Mark where you parked your vehicle',
        placePin: language === 'el' ? 'Τοποθέτηση Πινέζας' : 'Place Pin',
        cancelPin: language === 'el' ? 'Ακύρωση' : 'Cancel',
        myLocation: language === 'el' ? 'Η Τοποθεσία μου' : 'My Location',
        saveParking: language === 'el' ? 'Αποθήκευση Στάθμευσης' : 'Save Parking',
        selectVehicle: language === 'el' ? 'Επιλέξτε Όχημα' : 'Select Vehicle',
        addNote: language === 'el' ? 'Προσθήκη Σημείωσης (προαιρετικό)' : 'Add Note (optional)',
        notePlaceholder: language === 'el' ? 'π.χ. Κοντά στο φαρμακείο, 2ος όροφος...' : 'e.g. Near pharmacy, 2nd floor...',
        save: language === 'el' ? 'Αποθήκευση' : 'Save',
        cancel: language === 'el' ? 'Ακύρωση' : 'Cancel',
        currentParking: language === 'el' ? 'Τρέχουσα Στάθμευση' : 'Current Parking',
        parkingHistory: language === 'el' ? 'Ιστορικό Στάθμευσης' : 'Parking History',
        clearParking: language === 'el' ? 'Διαγραφή' : 'Clear',
        noVehicles: language === 'el' ? 'Δεν έχετε οχήματα. Προσθέστε ένα όχημα πρώτα.' : 'No vehicles. Add a vehicle first.',
        clickToPlace: language === 'el' ? 'Κάντε κλικ στον χάρτη για να τοποθετήσετε πινέζα' : 'Click on the map to place a pin',
        parkedAt: language === 'el' ? 'Παρκαρισμένο στις' : 'Parked at',
        navigate: language === 'el' ? 'Πλοήγηση' : 'Navigate',
        noActiveParking: language === 'el' ? 'Δεν υπάρχει ενεργή στάθμευση' : 'No active parking'
    };

    // Fetch vehicles and parking locations
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [vehiclesRes, parkingRes] = await Promise.all([
                api.get('/vehicles'),
                api.get('/parking-locations')
            ]);
            
            setVehicles(vehiclesRes.data || []);
            setParkingLocations(parkingRes.data || []);
            
            // Find active parking (most recent that's not cleared)
            const active = (parkingRes.data || []).find(p => p.is_active);
            setActiveParking(active || null);
            
            // Set default vehicle if only one
            if (vehiclesRes.data?.length === 1) {
                setSelectedVehicle(vehiclesRes.data[0].id);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Get current location
    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setMapCenter([latitude, longitude]);
                    toast.success(language === 'el' ? 'Τοποθεσία εντοπίστηκε!' : 'Location found!');
                },
                (error) => {
                    console.error('Geolocation error:', error);
                    toast.error(language === 'el' ? 'Δεν ήταν δυνατός ο εντοπισμός' : 'Could not get location');
                },
                { enableHighAccuracy: true }
            );
        }
    };

    // Handle map click to place pin
    const handleMapClick = (latlng) => {
        setTempPin({ lat: latlng.lat, lng: latlng.lng });
        setIsPlacingPin(false);
        setShowSaveModal(true);
    };

    // Save parking location
    const handleSaveParking = async () => {
        if (!tempPin) return;
        if (!selectedVehicle) {
            toast.error(language === 'el' ? 'Επιλέξτε όχημα' : 'Select a vehicle');
            return;
        }

        setSavingLocation(true);
        try {
            const response = await api.post('/parking-locations', {
                vehicle_id: selectedVehicle,
                latitude: tempPin.lat,
                longitude: tempPin.lng,
                note: locationNote || null
            });

            toast.success(language === 'el' ? 'Η στάθμευση αποθηκεύτηκε!' : 'Parking saved!');
            setShowSaveModal(false);
            setTempPin(null);
            setLocationNote('');
            fetchData();
        } catch (error) {
            console.error('Error saving parking:', error);
            toast.error(error.response?.data?.detail || (language === 'el' ? 'Αποτυχία αποθήκευσης' : 'Failed to save'));
        } finally {
            setSavingLocation(false);
        }
    };

    // Clear active parking
    const handleClearParking = async (parkingId) => {
        try {
            await api.delete(`/parking-locations/${parkingId}`);
            toast.success(language === 'el' ? 'Η στάθμευση διαγράφηκε' : 'Parking cleared');
            fetchData();
        } catch (error) {
            console.error('Error clearing parking:', error);
            toast.error(language === 'el' ? 'Αποτυχία διαγραφής' : 'Failed to clear');
        }
    };

    // Open navigation to parking location
    const openNavigation = (lat, lng) => {
        const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
        window.open(url, '_blank');
    };

    // Get vehicle name by id
    const getVehicleName = (vehicleId) => {
        const vehicle = vehicles.find(v => v.id === vehicleId);
        return vehicle ? `${vehicle.make} ${vehicle.model} (${vehicle.license_plate})` : 'Unknown';
    };

    return (
        <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
            {/* Header - Mobile optimized */}
            <div className="flex flex-col gap-3">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <ParkingCircle className="w-6 sm:w-7 h-6 sm:h-7 text-violet-600" />
                        {labels.title}
                    </h1>
                    <p className="text-slate-500 mt-1 text-sm">{labels.subtitle}</p>
                </div>
                
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        onClick={getCurrentLocation}
                        className="border-slate-300 flex-1 sm:flex-none"
                        size="sm"
                    >
                        <Locate className="w-4 h-4 mr-2" />
                        <span className="hidden sm:inline">{labels.myLocation}</span>
                        <span className="sm:hidden">GPS</span>
                    </Button>
                    
                    {!isPlacingPin ? (
                        <Button
                            onClick={() => setIsPlacingPin(true)}
                            className="bg-violet-600 hover:bg-violet-500 text-white flex-1 sm:flex-none"
                            disabled={vehicles.length === 0}
                            data-testid="place-pin-btn"
                            size="sm"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            {labels.placePin}
                        </Button>
                    ) : (
                        <Button
                            variant="outline"
                            onClick={() => setIsPlacingPin(false)}
                            className="border-red-300 text-red-600 hover:bg-red-50 flex-1 sm:flex-none"
                            size="sm"
                        >
                            <X className="w-4 h-4 mr-2" />
                            {labels.cancelPin}
                        </Button>
                    )}
                </div>
            </div>

            {/* Placing pin instruction */}
            {isPlacingPin && (
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-violet-600 animate-bounce" />
                    <span className="text-violet-800 font-medium">{labels.clickToPlace}</span>
                </div>
            )}

            {/* No vehicles warning */}
            {vehicles.length === 0 && !loading && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                    <Car className="w-5 h-5 text-amber-600" />
                    <span className="text-amber-800">{labels.noVehicles}</span>
                </div>
            )}

            {/* Current Active Parking Card - Mobile optimized */}
            {activeParking && (
                <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl p-3 sm:p-4 text-white">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
                                <ParkingCircle className="w-4 sm:w-5 h-4 sm:h-5 flex-shrink-0" />
                                {labels.currentParking}
                            </h3>
                            <p className="text-white/80 text-xs sm:text-sm mt-1 truncate">
                                {getVehicleName(activeParking.vehicle_id)}
                            </p>
                            <p className="text-white/70 text-xs mt-1 flex items-center gap-1">
                                <Clock className="w-3 h-3 flex-shrink-0" />
                                {labels.parkedAt} {format(new Date(activeParking.created_at), 'dd/MM HH:mm', { locale: language === 'el' ? el : enUS })}
                            </p>
                            {activeParking.note && (
                                <p className="text-white/80 text-xs sm:text-sm mt-2 italic truncate">&quot;{activeParking.note}&quot;</p>
                            )}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => openNavigation(activeParking.latitude, activeParking.longitude)}
                                className="bg-white/20 hover:bg-white/30 text-white border-0 flex-1 sm:flex-none"
                            >
                                <Navigation className="w-4 h-4 sm:mr-1" />
                                <span className="hidden sm:inline">{labels.navigate}</span>
                            </Button>
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleClearParking(activeParking.id)}
                                className="bg-white/20 hover:bg-red-500/50 text-white border-0"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Map - Responsive height with low z-index */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm relative" style={{ height: 'calc(100vh - 350px)', minHeight: '300px', maxHeight: '500px', zIndex: 1 }}>
                <MapContainer
                    center={mapCenter}
                    zoom={13}
                    style={{ height: '100%', width: '100%', zIndex: 1 }}
                    className={isPlacingPin ? 'cursor-crosshair' : ''}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    <CenterMap center={mapCenter} />
                    <MapClickHandler onMapClick={handleMapClick} isPlacingPin={isPlacingPin} />
                    
                    {/* Temp pin while placing */}
                    {tempPin && (
                        <Marker 
                            position={[tempPin.lat, tempPin.lng]} 
                            icon={activeParkingIcon}
                        />
                    )}
                    
                    {/* Active parking marker */}
                    {activeParking && (
                        <Marker
                            position={[activeParking.latitude, activeParking.longitude]}
                            icon={activeParkingIcon}
                        >
                            <Popup>
                                <div className="text-center p-2">
                                    <ParkingCircle className="w-6 h-6 text-violet-600 mx-auto mb-2" />
                                    <p className="font-semibold text-slate-800">{getVehicleName(activeParking.vehicle_id)}</p>
                                    <p className="text-xs text-slate-500 mt-1">
                                        {format(new Date(activeParking.created_at), 'dd/MM HH:mm')}
                                    </p>
                                    {activeParking.note && (
                                        <p className="text-sm text-slate-600 mt-2 italic">&quot;{activeParking.note}&quot;</p>
                                    )}
                                    <Button
                                        size="sm"
                                        onClick={() => openNavigation(activeParking.latitude, activeParking.longitude)}
                                        className="mt-2 bg-violet-600 hover:bg-violet-500 text-white w-full"
                                    >
                                        <Navigation className="w-3 h-3 mr-1" />
                                        {labels.navigate}
                                    </Button>
                                </div>
                            </Popup>
                        </Marker>
                    )}
                    
                    {/* History markers (inactive) */}
                    {parkingLocations
                        .filter(p => !p.is_active && p.id !== activeParking?.id)
                        .slice(0, 5)
                        .map((parking) => (
                            <Marker
                                key={parking.id}
                                position={[parking.latitude, parking.longitude]}
                                icon={historyParkingIcon}
                                opacity={0.6}
                            >
                                <Popup>
                                    <div className="text-center p-2">
                                        <History className="w-5 h-5 text-slate-400 mx-auto mb-2" />
                                        <p className="font-medium text-slate-600">{getVehicleName(parking.vehicle_id)}</p>
                                        <p className="text-xs text-slate-400 mt-1">
                                            {format(new Date(parking.created_at), 'dd/MM/yyyy HH:mm')}
                                        </p>
                                    </div>
                                </Popup>
                            </Marker>
                        ))}
                </MapContainer>
            </div>

            {/* Parking History */}
            {parkingLocations.filter(p => !p.is_active).length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="p-4 border-b border-slate-200 flex items-center gap-2">
                        <History className="w-4 h-4 text-slate-500" />
                        <h3 className="font-medium text-slate-700">{labels.parkingHistory}</h3>
                    </div>
                    <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                        {parkingLocations
                            .filter(p => !p.is_active)
                            .slice(0, 10)
                            .map((parking) => (
                                <div key={parking.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                            <MapPin className="w-5 h-5 text-slate-400" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-slate-700">
                                                {getVehicleName(parking.vehicle_id)}
                                            </p>
                                            <p className="text-xs text-slate-500">
                                                {format(new Date(parking.created_at), 'dd/MM/yyyy HH:mm', { locale: language === 'el' ? el : enUS })}
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => openNavigation(parking.latitude, parking.longitude)}
                                        className="text-slate-500 hover:text-violet-600"
                                    >
                                        <Navigation className="w-4 h-4" />
                                    </Button>
                                </div>
                            ))}
                    </div>
                </div>
            )}

            {/* Save Parking Modal */}
            <Dialog open={showSaveModal} onOpenChange={(open) => !savingLocation && setShowSaveModal(open)}>
                <DialogContent className="bg-white border-slate-200 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-slate-800 flex items-center gap-2">
                            <ParkingCircle className="w-5 h-5 text-violet-600" />
                            {labels.saveParking}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {/* Vehicle Selection */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">{labels.selectVehicle}</Label>
                            <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                                <SelectTrigger className="bg-white border-slate-300">
                                    <SelectValue placeholder={labels.selectVehicle} />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                    {vehicles.map((vehicle) => (
                                        <SelectItem key={vehicle.id} value={vehicle.id}>
                                            <div className="flex items-center gap-2">
                                                <Car className="w-4 h-4 text-slate-400" />
                                                {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Note */}
                        <div className="space-y-2">
                            <Label className="text-slate-700">{labels.addNote}</Label>
                            <Input
                                value={locationNote}
                                onChange={(e) => setLocationNote(e.target.value)}
                                placeholder={labels.notePlaceholder}
                                className="bg-white border-slate-300"
                            />
                        </div>

                        {/* Location Preview */}
                        {tempPin && (
                            <div className="bg-slate-50 rounded-lg p-3 text-sm">
                                <p className="text-slate-600">
                                    <MapPin className="w-4 h-4 inline mr-1 text-violet-600" />
                                    {tempPin.lat.toFixed(6)}, {tempPin.lng.toFixed(6)}
                                </p>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowSaveModal(false);
                                    setTempPin(null);
                                    setLocationNote('');
                                }}
                                disabled={savingLocation}
                                className="flex-1 border-slate-300"
                            >
                                {labels.cancel}
                            </Button>
                            <Button
                                onClick={handleSaveParking}
                                disabled={savingLocation || !selectedVehicle}
                                className="flex-1 bg-violet-600 hover:bg-violet-500 text-white"
                                data-testid="save-parking-btn"
                            >
                                {savingLocation ? (
                                    <span className="animate-spin mr-2">⏳</span>
                                ) : (
                                    <Save className="w-4 h-4 mr-2" />
                                )}
                                {labels.save}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
